import { useState, useEffect } from 'react';
import { Container, Paper, Stack, Text, Button, Group, Radio, Select, Loader, Alert } from '@mantine/core';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { IconAlertCircle } from '@tabler/icons-react';

// ステップの型定義
type Step = 'name' | 'workMode' | 'clockInTime' | 'breakTime' | 'confirm' | 'complete';

// 勤務形態の型定義
type WorkMode = 'remote' | 'onsite';

// 打刻データの型定義
interface ClockInData {
  name: string;
  workMode: WorkMode | null;
  clockInTime: string | null;
  breakMinutes: number | null;
}

export function ClockInDialog() {
  const { user, loading } = useAuth();
  const [currentStep, setCurrentStep] = useState<Step>('name');
  const [clockInData, setClockInData] = useState<ClockInData>({
    name: '',
    workMode: null,
    clockInTime: null,
    breakMinutes: null,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  // ユーザー情報が読み込まれたら名前を設定
  useEffect(() => {
    if (user) {
      setClockInData(prev => ({ ...prev, name: user.name }));
    }
  }, [user]);

  // ローディング中はローダーを表示
  if (loading) {
    return (
      <Container size="sm" py="xl">
        <Paper p="xl" withBorder>
          <Stack align="center" gap="md">
            <Loader size="lg" />
            <Text>読み込み中...</Text>
          </Stack>
        </Paper>
      </Container>
    );
  }

  // ユーザーが未認証の場合はログインページにリダイレクト
  if (!user) {
    navigate('/staff/login');
    return null;
  }

  // 現在時刻を取得（HH:MM形式）
  const getCurrentTime = () => {
    const now = new Date();
    const hours = now.getHours().toString().padStart(2, '0');
    const minutes = now.getMinutes().toString().padStart(2, '0');
    return `${hours}:${minutes}`;
  };

  // 打刻データをAPIに送信
  const submitClockInData = async () => {
    setIsSubmitting(true);
    setError(null);

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('認証トークンが見つかりません');
      }

      const response = await fetch(`${import.meta.env.VITE_API_URL}/api/clock-in`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          userId: user.id,
          name: clockInData.name,
          workMode: clockInData.workMode,
          clockInTime: clockInData.clockInTime,
          breakMinutes: clockInData.breakMinutes,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || '打刻データの送信に失敗しました');
      }

      setCurrentStep('complete');
    } catch (err) {
      setError(err instanceof Error ? err.message : '予期せぬエラーが発生しました');
    } finally {
      setIsSubmitting(false);
    }
  };

  // 次のステップに進む
  const goToNextStep = () => {
    switch (currentStep) {
      case 'name':
        setCurrentStep('workMode');
        break;
      case 'workMode':
        setCurrentStep('clockInTime');
        break;
      case 'clockInTime':
        setCurrentStep('breakTime');
        break;
      case 'breakTime':
        setCurrentStep('confirm');
        break;
      case 'confirm':
        submitClockInData();
        break;
      case 'complete':
        navigate('/staff/dashboard');
        break;
    }
  };

  // 前のステップに戻る
  const goToPreviousStep = () => {
    switch (currentStep) {
      case 'workMode':
        setCurrentStep('name');
        break;
      case 'clockInTime':
        setCurrentStep('workMode');
        break;
      case 'breakTime':
        setCurrentStep('clockInTime');
        break;
      case 'confirm':
        setCurrentStep('breakTime');
        break;
      default:
        break;
    }
  };

  // 勤務形態を更新
  const updateWorkMode = (value: WorkMode) => {
    setClockInData({ ...clockInData, workMode: value });
  };

  // 出勤時間を更新
  const updateClockInTime = (value: string) => {
    setClockInData({ ...clockInData, clockInTime: value });
  };

  // 休憩時間を更新
  const updateBreakTime = (value: string) => {
    setClockInData({ ...clockInData, breakMinutes: parseInt(value) });
  };

  // 各ステップのコンポーネントをレンダリング
  const renderStep = () => {
    switch (currentStep) {
      case 'name':
        return (
          <Stack gap="md">
            <Text size="xl" fw={500}>
              こんにちは！{clockInData.name}さんでよろしいですか？
            </Text>
            <Group justify="flex-end">
              <Button onClick={goToNextStep}>はい</Button>
            </Group>
          </Stack>
        );

      case 'workMode':
        return (
          <Stack gap="md">
            <Text size="xl" fw={500}>
              本日はテレワークですか？それとも出社ですか？
            </Text>
            <Radio.Group
              value={clockInData.workMode || ''}
              onChange={(value) => updateWorkMode(value as WorkMode)}
            >
              <Stack gap="xs">
                <Radio value="remote" label="テレワーク" />
                <Radio value="onsite" label="出社" />
              </Stack>
            </Radio.Group>
            <Group justify="space-between">
              <Button variant="subtle" onClick={goToPreviousStep}>
                戻る
              </Button>
              <Button
                onClick={goToNextStep}
                disabled={!clockInData.workMode}
              >
                次へ
              </Button>
            </Group>
          </Stack>
        );

      case 'clockInTime':
        return (
          <Stack gap="md">
            <Text size="xl" fw={500}>
              出勤時間を選んでください（現在時刻：{getCurrentTime()}）
            </Text>
            <Select
              placeholder="時間を選択"
              value={clockInData.clockInTime || ''}
              onChange={(value) => updateClockInTime(value || '')}
              data={[
                { value: '09:00', label: '09:00' },
                { value: '09:30', label: '09:30' },
                { value: '10:00', label: '10:00' },
                { value: getCurrentTime(), label: `現在時刻（${getCurrentTime()}）` },
              ]}
            />
            <Group justify="space-between">
              <Button variant="subtle" onClick={goToPreviousStep}>
                戻る
              </Button>
              <Button
                onClick={goToNextStep}
                disabled={!clockInData.clockInTime}
              >
                次へ
              </Button>
            </Group>
          </Stack>
        );

      case 'breakTime':
        return (
          <Stack gap="md">
            <Text size="xl" fw={500}>
              休憩時間を選んでください
            </Text>
            <Select
              placeholder="時間を選択"
              value={clockInData.breakMinutes?.toString() || ''}
              onChange={(value) => updateBreakTime(value || '')}
              data={[
                { value: '0', label: '0分' },
                { value: '30', label: '30分' },
                { value: '60', label: '60分' },
              ]}
            />
            <Group justify="space-between">
              <Button variant="subtle" onClick={goToPreviousStep}>
                戻る
              </Button>
              <Button
                onClick={goToNextStep}
                disabled={clockInData.breakMinutes === null}
              >
                次へ
              </Button>
            </Group>
          </Stack>
        );

      case 'confirm':
        return (
          <Stack gap="md">
            <Text size="xl" fw={500}>
              以下の内容で出勤を記録します。よろしいですか？
            </Text>
            <Paper p="md" withBorder>
              <Stack gap="xs">
                <Text>氏名：{clockInData.name}</Text>
                <Text>
                  出勤時間：{clockInData.clockInTime}
                </Text>
                <Text>
                  勤務形態：{clockInData.workMode === 'remote' ? 'テレワーク' : '出社'}
                </Text>
                <Text>
                  休憩時間：{clockInData.breakMinutes}分
                </Text>
              </Stack>
            </Paper>
            {error && (
              <Alert icon={<IconAlertCircle size={16} />} title="エラー" color="red">
                {error}
              </Alert>
            )}
            <Group justify="space-between">
              <Button variant="subtle" onClick={goToPreviousStep}>
                修正する
              </Button>
              <Button 
                onClick={goToNextStep}
                loading={isSubmitting}
                disabled={isSubmitting}
              >
                はい
              </Button>
            </Group>
          </Stack>
        );

      case 'complete':
        return (
          <Stack gap="md" align="center">
            <Text size="xl" fw={500}>
              出勤を記録しました！
            </Text>
            <Text>今日も一日がんばってください ☀️</Text>
            <Button onClick={goToNextStep}>ダッシュボードへ</Button>
          </Stack>
        );

      default:
        return null;
    }
  };

  return (
    <Container size="sm" py="xl">
      <Paper p="xl" withBorder>
        {renderStep()}
      </Paper>
    </Container>
  );
} 